import * as React from 'react';

/**
 * Intercepts the `render()` method of the derived component class, or function component, with
 * functionality to determine whether the component has rendered (returned an `Element`) or not
 * (returned `null`).
 *
 * @param ConsumerComponent A consumer class or function component.
 * @param renderSpy A function to be called whenever the component renders, exposing a
 *  `componentDidRender` argument.
 */

const withNullCheckRenderSpy = <TProps>(
    ConsumerComponent: React.ComponentType<TProps>,
    renderSpy: (componentDidRender: boolean) => void
): React.ComponentType<TProps> | React.MemoExoticComponent<React.FC<TProps>> => {
    if (ConsumerComponent.prototype && ConsumerComponent.prototype.isReactComponent) {
        // Extend the consumer component class and override its `render()` method

        class WithNullCheckRenderSpyAsClass extends (ConsumerComponent as React.ComponentClass<TProps>) {
            public static displayName = ConsumerComponent.displayName || ConsumerComponent.name;

            public render(): React.ReactNode {
                const output = super.render();

                renderSpy(output !== null);

                return output;
            }
        }

        return WithNullCheckRenderSpyAsClass;
    }

    const WithNullCheckRenderSpyAsFunction: React.FunctionComponent<TProps> = (props: TProps) => {
        const output = (ConsumerComponent as React.FunctionComponent<TProps>)(props);

        renderSpy(output !== null);

        return output;
    };

    WithNullCheckRenderSpyAsFunction.displayName = ConsumerComponent.displayName || ConsumerComponent.name;

    return React.memo(WithNullCheckRenderSpyAsFunction);
};

export default withNullCheckRenderSpy;
